// components/AccessListChanges.js - Access list SID changes view
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search, AlertTriangle, Copy } from 'lucide-react';
import { styles } from '../styles';
import { fetchAccessListDiff, refreshAccessListDiff } from '../services/api/accessListApi';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const AccessListChanges = () => {
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('new');
    const [query, setQuery] = useState('');
    const [diffData, setDiffData] = useState({
        newSids: [],
        missingSids: [],
        changedSids: [],
        frequentLimitChanges: [],
        limitsWatchlist: {
            all: [],
            compareWindows: ['snapshot', '24h', '7d']
        },
        growth: {
            topGrowingSids: [],
            topGrowingDestinations: [],
            sidGrowthHistory: {}
        },
        meta: null
    });
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [sidLimitsPopupOpen, setSidLimitsPopupOpen] = useState(false);
    const [sidLimitsPopupData, setSidLimitsPopupData] = useState(null);
    const [limitsSortBy, setLimitsSortBy] = useState('destinations');
    const [limitsMinDestinations, setLimitsMinDestinations] = useState('0');
    const [limitsSeverityFilter, setLimitsSeverityFilter] = useState('all');
    const [limitsCompareWindow, setLimitsCompareWindow] = useState('snapshot');
    const [popupLimitQuery, setPopupLimitQuery] = useState('');
    const [popupSortBy, setPopupSortBy] = useState('daily');
    const [toast, setToast] = useState({ visible: false, message: '' });
    const [nextFetchAt, setNextFetchAt] = useState(Date.now() + REFRESH_INTERVAL_MS);
    const [nowMs, setNowMs] = useState(Date.now());
    const refreshAbortRef = useRef(null);
    const refreshInFlightRef = useRef(false);

    const fetchDiff = useCallback(async (forceRefresh = false) => {
        if (refreshInFlightRef.current) return;
        refreshInFlightRef.current = true;
        if (forceRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError('');

        try {
            if (refreshAbortRef.current) {
                refreshAbortRef.current.abort();
            }
            const controller = new AbortController();
            refreshAbortRef.current = controller;
            let result;
            if (forceRefresh) {
                result = await refreshAccessListDiff(controller.signal, 'frontend-access-list-changes');
            } else {
                result = await fetchAccessListDiff(controller.signal);
            }

            setDiffData({
                newSids: result.newSids || [],
                missingSids: result.missingSids || [],
                changedSids: result.changedSids || [],
                frequentLimitChanges: result.frequentLimitChanges || [],
                limitsWatchlist: result.limitsWatchlist || {
                    all: [],
                    compareWindows: ['snapshot', '24h', '7d']
                },
                growth: result.growth || {
                    topGrowingSids: [],
                    topGrowingDestinations: [],
                    sidGrowthHistory: {}
                },
                meta: result.meta || null
            });
            setNextFetchAt(Date.now() + REFRESH_INTERVAL_MS);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError(err.message);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
            refreshInFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchDiff();
        const intervalId = setInterval(() => {
            fetchDiff(true);
        }, REFRESH_INTERVAL_MS);
        return () => {
            clearInterval(intervalId);
            if (refreshAbortRef.current) {
                refreshAbortRef.current.abort();
            }
        };
    }, [fetchDiff]);

    useEffect(() => {
        const ticker = setInterval(() => {
            setNowMs(Date.now());
        }, 1000);
        return () => clearInterval(ticker);
    }, []);

    const countdownText = useMemo(() => {
        const remainingMs = Math.max(0, nextFetchAt - nowMs);
        const totalSeconds = Math.floor(remainingMs / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${minutes}:${seconds}`;
    }, [nextFetchAt, nowMs]);

    useEffect(() => {
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                closePopup();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    const listToRender = activeTab === 'new'
        ? diffData.newSids
        : activeTab === 'missing'
            ? diffData.missingSids
            : diffData.changedSids;

    const limitsRows = useMemo(() => {
        const rows = [];
        (diffData.changedSids || []).forEach((sidEntry) => {
            const limitChanges = sidEntry?.changeSummary?.limitChanges || [];
            limitChanges.forEach((change) => {
                rows.push({
                    sid: sidEntry.sid,
                    destination: change.destination,
                    dailyLimitFrom: change.dailyLimitFrom || '',
                    dailyLimitTo: change.dailyLimitTo || '',
                    hourlyLimitFrom: change.hourlyLimitFrom || '',
                    hourlyLimitTo: change.hourlyLimitTo || '',
                    changedDailyLimit: Boolean(change.changedDailyLimit),
                    changedHourlyLimit: Boolean(change.changedHourlyLimit),
                    flagged: Boolean(change.flagged)
                });
            });
        });
        return rows;
    }, [diffData.changedSids]);

    const groupedLimits = useMemo(() => {
        const numericFromText = (value) => {
            if (value === null || value === undefined) return 0;
            const matches = String(value).match(/\d+(?:\.\d+)?/g);
            if (!matches?.length) return 0;
            const nums = matches.map((item) => Number(item)).filter((item) => Number.isFinite(item));
            return nums.length ? Math.max(...nums) : 0;
        };

        const bySid = new Map();
        limitsRows.forEach((row) => {
            if (!bySid.has(row.sid)) {
                bySid.set(row.sid, {
                    sid: row.sid,
                    rows: [],
                    dailyValues: [],
                    hourlyValues: []
                });
            }
            const current = bySid.get(row.sid);
            current.rows.push(row);
            const dailyNum = numericFromText(row.dailyLimitTo || row.dailyLimitFrom || 0);
            const hourlyNum = numericFromText(row.hourlyLimitTo || row.hourlyLimitFrom || 0);
            if (Number.isFinite(dailyNum) && dailyNum > 0) current.dailyValues.push(dailyNum);
            if (Number.isFinite(hourlyNum) && hourlyNum > 0) current.hourlyValues.push(hourlyNum);
        });

        const toAverageValue = (values) => {
            if (!values.length) return '--';
            const average = values.reduce((sum, value) => sum + value, 0) / values.length;
            return average.toFixed(2);
        };

        const toTotalValue = (values) => {
            if (!values.length) return '--';
            return values.reduce((sum, value) => sum + value, 0).toFixed(2);
        };

        return Array.from(bySid.values()).map((entry) => ({
            sid: entry.sid,
            details: entry.rows,
            destinationsCount: entry.rows.length,
            avgTotalDailyLimit: toAverageValue(entry.dailyValues),
            avgTotalHotLimit: toAverageValue(entry.hourlyValues),
            totalDailyLimit: toTotalValue(entry.dailyValues),
            totalHotLimit: toTotalValue(entry.hourlyValues),
            avgTotalDailyLimitNumeric: entry.dailyValues.length ? entry.dailyValues.reduce((sum, value) => sum + value, 0) / entry.dailyValues.length : 0,
            avgTotalHotLimitNumeric: entry.hourlyValues.length ? entry.hourlyValues.reduce((sum, value) => sum + value, 0) / entry.hourlyValues.length : 0,
            totalDailyLimitNumeric: entry.dailyValues.reduce((sum, value) => sum + value, 0),
            totalHotLimitNumeric: entry.hourlyValues.reduce((sum, value) => sum + value, 0)
        }));
    }, [limitsRows]);

    const frequentLimitChangeMap = useMemo(() => {
        const map = new Map();
        (diffData.frequentLimitChanges || []).forEach((entry) => {
            map.set(entry.sid, entry);
        });
        return map;
    }, [diffData.frequentLimitChanges]);

    const filteredList = useMemo(() => {
        if (!query.trim()) return listToRender;
        const lowered = query.toLowerCase();
        return listToRender.filter((sid) => {
            const searchEntry = sid.current || sid;
            if (sid.sid.toLowerCase().includes(lowered)) return true;
            return searchEntry.destinations?.some((dest) =>
                dest.destination.toLowerCase().includes(lowered) ||
                dest.ranges?.some((range) => range.toLowerCase().includes(lowered))
            );
        });
    }, [listToRender, query]);

    const filteredLimits = useMemo(() => {
        if (!query.trim()) return groupedLimits;
        const lowered = query.toLowerCase();
        return groupedLimits.filter((row) => (
            row.sid.toLowerCase().includes(lowered) ||
            String(row.avgTotalDailyLimit).toLowerCase().includes(lowered) ||
            String(row.avgTotalHotLimit).toLowerCase().includes(lowered) ||
            String(row.totalDailyLimit).toLowerCase().includes(lowered) ||
            String(row.totalHotLimit).toLowerCase().includes(lowered) ||
            row.details.some((detail) => detail.destination.toLowerCase().includes(lowered))
        ));
    }, [groupedLimits, query]);

    const watchlistMap = useMemo(() => {
        const map = new Map();
        (diffData.limitsWatchlist?.all || []).forEach((item) => {
            map.set(item.sid, item);
        });
        return map;
    }, [diffData.limitsWatchlist]);

    const severityCounts = useMemo(() => {
        const counts = { critical: 0, warning: 0, info: 0 };
        (diffData.limitsWatchlist?.all || []).forEach((item) => {
            if (item.severity === 'critical') counts.critical += 1;
            else if (item.severity === 'warning') counts.warning += 1;
            else counts.info += 1;
        });
        return counts;
    }, [diffData.limitsWatchlist]);

    const parseLimitValue = (value) => {
        if (!value || value === '--') return 0;
        const numbers = String(value).match(/\d+(?:\.\d+)?/g);
        if (!numbers?.length) return 0;
        return Math.max(...numbers.map((item) => Number(item)).filter((item) => Number.isFinite(item)));
    };

    const sortedFilteredLimits = useMemo(() => {
        const minDestinations = Number(limitsMinDestinations) || 0;
        const rows = filteredLimits.filter((row) => {
            if (row.destinationsCount < minDestinations) return false;
            if (limitsSeverityFilter === 'all') return true;
            const severity = watchlistMap.get(row.sid)?.severity || 'info';
            return severity === limitsSeverityFilter;
        });

        return [...rows].sort((a, b) => {
            if (limitsSortBy === 'daily') {
                return b.avgTotalDailyLimitNumeric - a.avgTotalDailyLimitNumeric;
            }
            if (limitsSortBy === 'hourly') {
                return b.avgTotalHotLimitNumeric - a.avgTotalHotLimitNumeric;
            }
            const countDiff = b.destinationsCount - a.destinationsCount;
            if (countDiff !== 0) return countDiff;
            return String(a.sid || '').localeCompare(String(b.sid || ''));
        });
    }, [filteredLimits, limitsMinDestinations, limitsSortBy, limitsSeverityFilter, watchlistMap]);

    const topSidSet = useMemo(() => {
        const ranked = [...sortedFilteredLimits]
            .sort((a, b) => {
                const scoreA = a.totalDailyLimitNumeric + a.totalHotLimitNumeric + (a.destinationsCount * 10);
                const scoreB = b.totalDailyLimitNumeric + b.totalHotLimitNumeric + (b.destinationsCount * 10);
                return scoreB - scoreA;
            })
            .slice(0, 10);

        const map = new Map();
        ranked.forEach((item, index) => {
            map.set(item.sid, index + 1);
        });
        return map;
    }, [sortedFilteredLimits]);

    const popupFilteredDetails = useMemo(() => {
        if (!sidLimitsPopupData?.details?.length) return [];

        const lowered = popupLimitQuery.trim().toLowerCase();
        const filtered = sidLimitsPopupData.details.filter((detail) => {
            if (!lowered) return true;
            const daily = `${detail.dailyLimitFrom || '--'} -> ${detail.dailyLimitTo || '--'}`.toLowerCase();
            const hourly = `${detail.hourlyLimitFrom || '--'} -> ${detail.hourlyLimitTo || '--'}`.toLowerCase();
            return detail.destination.toLowerCase().includes(lowered) || daily.includes(lowered) || hourly.includes(lowered);
        });

        return [...filtered].sort((a, b) => {
            const dailyA = parseLimitValue(a.dailyLimitTo || a.dailyLimitFrom || '0');
            const dailyB = parseLimitValue(b.dailyLimitTo || b.dailyLimitFrom || '0');
            const hourlyA = parseLimitValue(a.hourlyLimitTo || a.hourlyLimitFrom || '0');
            const hourlyB = parseLimitValue(b.hourlyLimitTo || b.hourlyLimitFrom || '0');

            if (popupSortBy === 'hourly') return hourlyB - hourlyA;
            if (popupSortBy === 'name') return String(a.destination || '').localeCompare(String(b.destination || ''));
            return dailyB - dailyA;
        });
    }, [sidLimitsPopupData, popupLimitQuery, popupSortBy]);

    const topDestinationSet = useMemo(() => {
        if (!popupFilteredDetails.length) return new Set();
        const ranked = [...popupFilteredDetails]
            .sort((a, b) => {
                const scoreA = parseLimitValue(a.dailyLimitTo || a.dailyLimitFrom || '0') + parseLimitValue(a.hourlyLimitTo || a.hourlyLimitFrom || '0');
                const scoreB = parseLimitValue(b.dailyLimitTo || b.dailyLimitFrom || '0') + parseLimitValue(b.hourlyLimitTo || b.hourlyLimitFrom || '0');
                return scoreB - scoreA;
            })
            .slice(0, 3)
            .map((item) => item.destination);
        return new Set(ranked);
    }, [popupFilteredDetails]);

    const topGrowingSids = useMemo(() => diffData.growth?.topGrowingSids || [], [diffData.growth]);
    const topGrowingDestinations = useMemo(() => diffData.growth?.topGrowingDestinations || [], [diffData.growth]);
    const destinationSummary = useMemo(() => diffData.growth?.destinationSummary || {
        day: [],
        threeDays: [],
        week: []
    }, [diffData.growth]);

    const splitDestination = (destination = '') => {
        const parts = destination.split(' - ').map((part) => part.trim()).filter(Boolean);
        return {
            country: parts[0] || destination,
            carrier: parts.slice(2).join(' - ') || parts[1] || 'General',
            label: parts.slice(0, 2).join(' - ') || destination
        };
    };

    const toCurrentOverallFormat = (value) => {
        const text = String(value || '').trim();
        if (!text) return '-- / --';
        if (text.includes('/')) return text;
        return `-- / ${text}`;
    };

    const showToast = (message) => {
        setToast({ visible: true, message });
        setTimeout(() => setToast({ visible: false, message: '' }), 2000);
    };

    const copyText = async (text, message) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(message || 'Copied to clipboard');
        } catch (err) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            showToast(message || 'Copied to clipboard');
        }
    };

    const openPopup = (sidEntry, dest) => {
        const info = splitDestination(dest.destination);
        setPopupData({
            sid: sidEntry.sid,
            destination: info.label,
            carrier: info.carrier,
            count: dest.count,
            ranges: dest.ranges || [],
            totalRows: sidEntry.totalRows,
            dailyLimit: toCurrentOverallFormat(dest.dailyCounter || dest.dailyLimit),
            hourlyLimit: toCurrentOverallFormat(dest.hourlyCounter || dest.hourlyLimit)
        });
        setPopupOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closePopup = () => {
        setPopupOpen(false);
        setPopupData(null);
        setSidLimitsPopupOpen(false);
        setSidLimitsPopupData(null);
        document.body.style.overflow = '';
    };

    const openSidLimitsPopup = (sidEntry) => {
        setSidLimitsPopupData(sidEntry);
        setPopupLimitQuery('');
        setPopupSortBy('daily');
        setSidLimitsPopupOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const copyAllRanges = () => {
        if (!popupData?.ranges?.length) return;
        copyText(popupData.ranges.join('\n'), `All ${popupData.ranges.length} ranges copied`);
    };

    const copySidRanges = (sidEntry) => {
        const sourceEntry = sidEntry.current || sidEntry;
        const ranges = (sourceEntry.destinations || [])
            .flatMap((dest) => dest.ranges || [])
            .filter(Boolean);
        const uniqueRanges = Array.from(new Set(ranges));
        if (uniqueRanges.length === 0) return;
        copyText(uniqueRanges.join('\n'), `All ${uniqueRanges.length} ranges copied`);
    };

    const sortedList = useMemo(() => {
        return [...filteredList].sort((a, b) => {
            const aEntry = a.current || a;
            const bEntry = b.current || b;
            const rowsDiff = (bEntry.totalRows || 0) - (aEntry.totalRows || 0);
            if (rowsDiff !== 0) return rowsDiff;
            const destDiff = (bEntry.destinations?.length || 0) - (aEntry.destinations?.length || 0);
            if (destDiff !== 0) return destDiff;
            return String(a.sid || '').localeCompare(String(b.sid || ''));
        });
    }, [filteredList]);

    const maskedSource = (source = '') => {
        if (!source) return '***';
        const parts = source.split('\\');
        if (parts.length <= 2) return '***';
        return `${parts.slice(0, -1).join('\\')}\\***`;
    };

    return (
        <div className="access-changes-root">
            <div className="access-bar">
                <div className="access-bar-top">
                    <div className="access-title">Access List Changes</div>
                    <div className="access-actions">
                        <div className="kpis">
                            <div className="kpi"><div className="v">{filteredList.length}</div><div className="l">Suppliers</div></div>
                            <div className="kpi"><div className="v">{filteredList.reduce((sum, entry) => sum + (entry.destinations?.length || 0), 0)}</div><div className="l">Routes</div></div>
                            <div className="kpi"><div className="v">{activeTab === 'new' ? diffData.newSids.length : activeTab === 'missing' ? diffData.missingSids.length : activeTab === 'changed' ? diffData.changedSids.length : filteredLimits.length}</div><div className="l">{activeTab === 'limits' ? 'Grouped SIDs' : 'SIDs'}</div></div>
                        </div>
                        <button className="refresh-btn" onClick={() => fetchDiff(true)} disabled={loading || refreshing}>
                            <RefreshCw size={14} style={(loading || refreshing) ? { animation: 'spin 1s linear infinite' } : {}} />
                            {(loading || refreshing) ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <div className="refresh-timer">Next fetch in {countdownText}</div>
                    </div>
                </div>
                <div className="access-meta">
                    {diffData.meta ? (
                        <>
                            Source: <span>{maskedSource(diffData.meta.sourceFile)}</span>
                            {' | '}Current SIDs: <span>{diffData.meta.totalCurrentSids}</span>
                            {' | '}Previous SIDs: <span>{diffData.meta.totalPreviousSids}</span>
                        </>
                    ) : (
                        'Source: -- | Current SIDs: -- | Previous SIDs: --'
                    )}
                </div>
                <div className="tab-bar">
                    <button
                        className={`tab-btn new-sids ${activeTab === 'new' ? 'active' : ''}`}
                        onClick={() => setActiveTab('new')}
                    >
                        New SIDs <span className="cnt">{diffData.newSids.length}</span>
                    </button>
                        <button
                            className={`tab-btn disappeared ${activeTab === 'missing' ? 'active' : ''}`}
                            onClick={() => setActiveTab('missing')}
                        >
                            Disappeared SIDs <span className="cnt">{diffData.missingSids.length}</span>
                        </button>
                        <button
                            className={`tab-btn changed ${activeTab === 'changed' ? 'active' : ''}`}
                            onClick={() => setActiveTab('changed')}
                        >
                            Changed SIDs <span className="cnt">{diffData.changedSids.length}</span>
                        </button>
                        <button
                            className={`tab-btn limits ${activeTab === 'limits' ? 'active' : ''}`}
                            onClick={() => setActiveTab('limits')}
                        >
                            Limits Monitor <span className="cnt">{limitsRows.length}</span>
                        </button>
                    <div className="search-wrap">
                        <Search size={14} />
                        <input
                            className="search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search SID, destination, or range"
                        />
                    </div>
                </div>
            </div>

            <div className="main">
                <div className="topbar"></div>

                <div className="section-label">Active Suppliers</div>

                {error && (
                    <div style={{ ...styles.errorAlert, marginBottom: '16px' }}>
                        <AlertTriangle size={18} />
                        {error}
                    </div>
                )}

                {loading ? (
                    <div style={styles.loadingContainer}>
                        <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ marginLeft: '10px' }}>Loading access list changes...</span>
                    </div>
                ) : (activeTab === 'limits' ? sortedFilteredLimits.length === 0 : filteredList.length === 0) ? (
                    <div style={styles.emptyState}>
                         No {activeTab === 'new' ? 'new' : activeTab === 'missing' ? 'disappeared' : activeTab === 'changed' ? 'changed' : 'limit'} entries found.
                    </div>
                ) : (
                    activeTab === 'limits' ? (
                        <>
                        <div className="growth-panels">
                            <div className="growth-panel">
                                <div className="growth-title">Top Growing SIDs</div>
                                <div className="growth-list">
                                    {topGrowingSids.slice(0, 5).map((item) => (
                                        <div key={`${item.sid}-${item.runAt}`} className="growth-row">
                                            <span className="growth-name">{item.sid}</span>
                                            <span className="growth-value">+{Number(item.growthTotal || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="growth-panel">
                                <div className="growth-title">Top Growing Destinations</div>
                                <div className="growth-list">
                                    {topGrowingDestinations.slice(0, 5).map((item) => (
                                        <div key={`${item.sid}-${item.destination}-${item.runAt}`} className="growth-row">
                                            <span className="growth-name">{item.destination}</span>
                                            <span className="growth-value">+{Number(item.growthTotal || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="growth-panels destination-summary-panels">
                            <div className="growth-panel">
                                <div className="growth-title">Destination Summary (1 Day)</div>
                                <div className="growth-list">
                                    {destinationSummary.day.slice(0, 5).map((item) => (
                                        <div key={`day-${item.destination}`} className="growth-row">
                                            <span className="growth-name">{item.destination}</span>
                                            <span className="growth-value">{Number(item.totalVolume || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="growth-panel">
                                <div className="growth-title">Destination Summary (3 Days)</div>
                                <div className="growth-list">
                                    {destinationSummary.threeDays.slice(0, 5).map((item) => (
                                        <div key={`three-${item.destination}`} className="growth-row">
                                            <span className="growth-name">{item.destination}</span>
                                            <span className="growth-value">{Number(item.totalVolume || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="growth-panel">
                                <div className="growth-title">Destination Summary (Week)</div>
                                <div className="growth-list">
                                    {destinationSummary.week.slice(0, 5).map((item) => (
                                        <div key={`week-${item.destination}`} className="growth-row">
                                            <span className="growth-name">{item.destination}</span>
                                            <span className="growth-value">{Number(item.totalVolume || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="limits-filter-bar">
                            <label className="limits-filter-item">
                                Sort by
                                <select value={limitsSortBy} onChange={(e) => setLimitsSortBy(e.target.value)}>
                                    <option value="destinations">Destinations</option>
                                    <option value="daily">Daily Limit</option>
                                    <option value="hourly">Hourly Limit</option>
                                </select>
                            </label>
                            <label className="limits-filter-item">
                                Severity
                                <select value={limitsSeverityFilter} onChange={(e) => setLimitsSeverityFilter(e.target.value)}>
                                    <option value="all">All</option>
                                    <option value="critical">Critical ({severityCounts.critical})</option>
                                    <option value="warning">Warning ({severityCounts.warning})</option>
                                    <option value="info">Info ({severityCounts.info})</option>
                                </select>
                            </label>
                            <label className="limits-filter-item">
                                Compare
                                <select value={limitsCompareWindow} onChange={(e) => setLimitsCompareWindow(e.target.value)}>
                                    {(diffData.limitsWatchlist?.compareWindows || ['snapshot', '24h', '7d']).map((windowKey) => (
                                        <option key={windowKey} value={windowKey}>{windowKey}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="limits-filter-item">
                                Min destinations
                                <select value={limitsMinDestinations} onChange={(e) => setLimitsMinDestinations(e.target.value)}>
                                    <option value="0">All</option>
                                    <option value="2">2+</option>
                                    <option value="3">3+</option>
                                    <option value="5">5+</option>
                                    <option value="10">10+</option>
                                </select>
                            </label>
                        </div>
                        <div className="limits-table-wrap">
                            <table className="limits-table">
                                <thead>
                                    <tr>
                                        <th>SID</th>
                                        <th>Severity</th>
                                        <th>Reason</th>
                                        <th>Avg Total Daily Limit</th>
                                        <th>Avg Total Hot Limit</th>
                                        <th>Destinations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedFilteredLimits.map((row, idx) => {
                                        const watch = watchlistMap.get(row.sid);
                                        const severity = watch?.severity || 'info';
                                        return (
                                        <tr key={`${row.sid}-${idx}`}>
                                            <td className="mono-cell">
                                                <button
                                                    className={`sid-open-btn ${topSidSet.get(row.sid) ? `sid-open-btn-hot sid-open-btn-hot-${topSidSet.get(row.sid)}` : ''}`}
                                                    type="button"
                                                    onClick={() => openSidLimitsPopup(row)}
                                                >
                                                    {row.sid}
                                                </button>
                                            </td>
                                            <td>
                                                <span className={`severity-pill ${severity}`}>{severity}</span>
                                            </td>
                                            <td>
                                                <span className="reason-cell">{watch?.reason || 'baseline snapshot comparison'}</span>
                                            </td>
                                            <td className="mono-cell">
                                                <span className={`limit-value-pill daily ${topSidSet.get(row.sid) ? `rank-${topSidSet.get(row.sid)}` : ''}`}>{row.avgTotalDailyLimit} / {row.totalDailyLimit}</span>
                                            </td>
                                            <td className="mono-cell">
                                                <span className={`limit-value-pill hourly ${topSidSet.get(row.sid) ? `rank-${topSidSet.get(row.sid)}` : ''}`}>{row.avgTotalHotLimit} / {row.totalHotLimit}</span>
                                            </td>
                                            <td>
                                                <span className={`destinations-pill ${topSidSet.get(row.sid) ? `rank-${topSidSet.get(row.sid)}` : ''} ${row.destinationsCount >= 10 ? 'tier-max' : row.destinationsCount >= 6 ? 'tier-high' : row.destinationsCount >= 3 ? 'tier-mid' : 'tier-low'}`}>
                                                    {row.destinationsCount}
                                                </span>
                                            </td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                        </div>
                        <div className="compare-window-note">Comparing by <span>{limitsCompareWindow}</span> window.</div>
                        </>
                    ) : (
                    <div className="grid" id="cardGrid">
                        {sortedList.map((sidEntry, index) => {
                            const displayEntry = activeTab === 'changed' ? (sidEntry.current || {}) : sidEntry;
                            const destinations = displayEntry.destinations || [];
                            const badgeClass = destinations.length > 1 ? 'dest-multi' : 'dest-single';
                            const frequent = frequentLimitChangeMap.get(sidEntry.sid);
                            const limitChanges = sidEntry.changeSummary?.limitChanges || [];
                            return (
                                <div className={`card ${frequent ? 'card-hot' : ''}`} key={`${sidEntry.sid}-${index}`} data-supplier={sidEntry.sid}>
                                    <div className="card-head">
                                        <div className="supplier">{sidEntry.sid}</div>
                                        <div className="badges">
                                            {frequent && <span className="badge hot">HOT</span>}
                                            <span className="badge rows">{displayEntry.totalRows || 0} rows</span>
                                            <span className={`badge ${badgeClass}`}>{destinations.length} dest</span>
                                            {activeTab === 'changed' && (
                                                <span className="badge limit">{limitChanges.length} limit changes</span>
                                            )}
                                            <button
                                                className="sid-copy-btn"
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    copySidRanges(sidEntry);
                                                }}
                                                title="Copy all ranges"
                                            >
                                                <Copy size={11} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {activeTab === 'changed' && limitChanges.length > 0 && (
                                            <div className="limit-change-list">
                                                {limitChanges.slice(0, 4).map((change, changeIdx) => (
                                                    <div key={`${sidEntry.sid}-${change.destination}-${changeIdx}`} className="limit-change-row">
                                                        <span className="limit-dest">{change.destination}</span>
                                                        <span className="limit-val">D: {change.dailyLimitFrom || '--'} → {change.dailyLimitTo || '--'}</span>
                                                        <span className="limit-val">H: {change.hourlyLimitFrom || '--'} → {change.hourlyLimitTo || '--'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {destinations.map((dest, destIndex) => {
                                            const info = splitDestination(dest.destination);
                                            const countClass = dest.count >= 6 ? 'high' : '';
                                            return (
                                                <div
                                                    className="route"
                                            key={`${sidEntry.sid}-${dest.destination}-${destIndex}`}
                                                    onClick={() => openPopup(displayEntry, dest)}
                                                >
                                                    <div className="route-left">
                                                        <div className="route-info">
                                                            <span className="route-country">{info.label}</span>
                                                            <span className="route-sep">-</span>
                                                            <span className="route-carrier">{info.carrier}</span>
                                                        </div>
                                                    </div>
                                                    <div className="route-right">
                                                        <span className={`count-pill ${countClass}`}>{dest.count}</span>
                                                        <span className="expand-icon">&gt;</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    )
                )}
            </div>

            <div className={`overlay ${popupOpen ? 'open' : ''}`} onClick={(e) => {
                if (e.target.classList.contains('overlay')) closePopup();
            }}>
                {popupData && (
                    <div className="popup">
                        <div className="popup-head">
                            <div className="popup-title-wrap">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="popup-title">
                                        {popupData.destination}{popupData.carrier ? ` - ${popupData.carrier}` : ''}
                                    </div>
                                    <button className="copy-all-btn" onClick={copyAllRanges}>
                                        <Copy size={11} />
                                        COPY ALL
                                    </button>
                                </div>
                                <div className="popup-sub">Supplier: {popupData.sid}</div>
                            </div>
                            <button className="popup-close" onClick={closePopup}>x</button>
                        </div>
                        <div className="popup-body">
                            <div className="popup-stats">
                                <div className="pstat"><div className="pv">{popupData.count}</div><div className="pl">Count</div></div>
                                <div className="pstat"><div className="pv">{popupData.ranges.length}</div><div className="pl">Ranges</div></div>
                                <div className="pstat"><div className="pv">{popupData.totalRows}</div><div className="pl">Rows</div></div>
                                <div className="pstat pstat-limit"><div className="pv">{popupData.dailyLimit}</div><div className="pl">Daily Limit</div></div>
                                <div className="pstat pstat-limit"><div className="pv">{popupData.hourlyLimit}</div><div className="pl">Hourly Limit</div></div>
                            </div>
                            <div className="range-section-label">Number Ranges</div>
                            <div className="range-grid">
                                {popupData.ranges.map((range) => (
                                    <div
                                        key={range}
                                        className="range-chip"
                                        onClick={() => copyText(range, `Copied: ${range}`)}
                                        title="Click to copy"
                                    >
                                        {range}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={`overlay ${sidLimitsPopupOpen ? 'open' : ''}`} onClick={(e) => {
                if (e.target.classList.contains('overlay')) closePopup();
            }}>
                {sidLimitsPopupData && (
                    <div className="popup limits-details-popup">
                        <div className="popup-head">
                            <div className="popup-title-wrap">
                                <div className="popup-title">SID Limits Details</div>
                                <div className="popup-sub">Supplier: {sidLimitsPopupData.sid}</div>
                            </div>
                            <button className="popup-close" onClick={closePopup}>x</button>
                        </div>
                        <div className="popup-body">
                            <div className="limits-popup-filter-bar">
                                <div className="limits-popup-search">
                                    <Search size={13} />
                                    <input
                                        value={popupLimitQuery}
                                        onChange={(e) => setPopupLimitQuery(e.target.value)}
                                        placeholder="Filter destination or limit"
                                    />
                                </div>
                                <label className="limits-filter-item compact">
                                    Sort by
                                    <select value={popupSortBy} onChange={(e) => setPopupSortBy(e.target.value)}>
                                        <option value="daily">Daily Limit</option>
                                        <option value="hourly">Hourly Limit</option>
                                        <option value="name">Destination</option>
                                    </select>
                                </label>
                            </div>
                            <div className="limits-detail-table-wrap">
                                <table className="limits-detail-table">
                                    <thead>
                                        <tr>
                                            <th>Destination</th>
                                            <th>Daily Limit</th>
                                            <th>Hourly Limit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {popupFilteredDetails.map((detail, detailIdx) => (
                                            <tr key={`${sidLimitsPopupData.sid}-${detail.destination}-${detailIdx}`}>
                                                <td>
                                                    <span className={`destination-name ${topDestinationSet.has(detail.destination) ? 'destination-name-top' : ''}`}>{detail.destination}</span>
                                                </td>
                                                <td className="mono-cell">
                                                    <span className={`popup-limit-pill daily ${topDestinationSet.has(detail.destination) ? 'top' : ''}`}>{detail.dailyLimitFrom || '--'} -> {detail.dailyLimitTo || '--'}</span>
                                                </td>
                                                <td className="mono-cell">
                                                    <span className={`popup-limit-pill hourly ${topDestinationSet.has(detail.destination) ? 'top' : ''}`}>{detail.hourlyLimitFrom || '--'} -> {detail.hourlyLimitTo || '--'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={`toast ${toast.visible ? 'show' : ''}`}>
                <span>{toast.message}</span>
            </div>

            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap');
                .access-changes-root {
                    background: #080b12;
                    min-height: 100vh;
                    font-family: 'DM Sans', sans-serif;
                    color: #e2e8f0;
                    background-image:
                        radial-gradient(ellipse 60% 40% at 80% 0%, rgba(59, 130, 246, 0.06) 0%, transparent 60%),
                        radial-gradient(ellipse 40% 30% at 0% 100%, rgba(6, 182, 212, 0.04) 0%, transparent 60%);
                }
                .access-bar {
                    background: #0a0d16;
                    border-bottom: 1px solid #1c2438;
                    padding: 14px 24px 0;
                }
                .access-bar-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 10px;
                }
                .access-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .access-title {
                    font-size: 17px;
                    font-weight: 700;
                    letter-spacing: 0.01em;
                }
                .refresh-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #f59e0b;
                    color: #000;
                    border: none;
                    border-radius: 6px;
                    padding: 7px 16px;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: opacity 0.15s, transform 0.15s;
                }
                .refresh-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .refresh-btn:hover:not(:disabled) {
                    opacity: 0.88;
                    transform: translateY(-1px);
                }
                .refresh-timer {
                    font-family: 'Space Mono', monospace;
                    font-size: 12px;
                    color: #93c5fd;
                    background: rgba(37, 99, 235, 0.15);
                    border: 1px solid rgba(59, 130, 246, 0.35);
                    border-radius: 6px;
                    padding: 7px 10px;
                    min-width: 145px;
                    text-align: center;
                }
                .card-hot {
                    animation: hotPulse 1.8s ease-in-out infinite;
                    border-color: rgba(239, 68, 68, 0.55) !important;
                }
                @keyframes hotPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.15); }
                    50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
                }
                .badge.hot {
                    background: rgba(239, 68, 68, 0.18);
                    color: #fecaca;
                    border: 1px solid rgba(239, 68, 68, 0.45);
                }
                .badge.limit {
                    background: rgba(59, 130, 246, 0.18);
                    color: #bfdbfe;
                    border: 1px solid rgba(59, 130, 246, 0.35);
                }
                .limit-change-list {
                    margin-bottom: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }
                .limit-change-row {
                    display: grid;
                    grid-template-columns: 1fr auto auto;
                    gap: 10px;
                    font-size: 11px;
                    background: rgba(15, 23, 42, 0.55);
                    border: 1px solid #1e293b;
                    border-radius: 5px;
                    padding: 6px 8px;
                }
                .limit-dest {
                    color: #f8fafc;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .limit-val {
                    color: #93c5fd;
                    font-family: 'Space Mono', monospace;
                }
                .limits-table-wrap {
                    background: #0b1220;
                    border: 1px solid #1e293b;
                    border-radius: 10px;
                    overflow: auto;
                }
                .limits-table {
                    width: 100%;
                    border-collapse: collapse;
                    min-width: 1050px;
                }
                .limits-table th,
                .limits-table td {
                    padding: 10px 12px;
                    border-bottom: 1px solid #1e293b;
                    text-align: left;
                    font-size: 12px;
                }
                .limits-table th {
                    font-size: 11px;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    color: #93c5fd;
                    background: #0f172a;
                    position: sticky;
                    top: 0;
                }
                .mono-cell {
                    font-family: 'Space Mono', monospace;
                    color: #cbd5e1;
                    white-space: nowrap;
                }
                .sid-open-btn {
                    background: transparent;
                    border: none;
                    color: #38bdf8;
                    font-family: 'Space Mono', monospace;
                    font-size: 12px;
                    padding: 0;
                    cursor: pointer;
                    text-decoration: underline;
                    text-decoration-color: rgba(56, 189, 248, 0.4);
                    text-underline-offset: 3px;
                }
                .sid-open-btn:hover {
                    color: #7dd3fc;
                }
                .sid-open-btn-hot {
                    color: #fde047;
                    text-decoration-color: rgba(253, 224, 71, 0.6);
                    animation: sidFlash 1.4s ease-in-out infinite;
                }
                .sid-open-btn-hot-1 { color: #fb7185; }
                .sid-open-btn-hot-2 { color: #fb923c; }
                .sid-open-btn-hot-3 { color: #facc15; }
                .sid-open-btn-hot-4 { color: #a3e635; }
                .sid-open-btn-hot-5 { color: #34d399; }
                .sid-open-btn-hot-6 { color: #2dd4bf; }
                .sid-open-btn-hot-7 { color: #22d3ee; }
                .sid-open-btn-hot-8 { color: #38bdf8; }
                .sid-open-btn-hot-9 { color: #60a5fa; }
                .sid-open-btn-hot-10 { color: #818cf8; }
                .limit-value-pill {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 999px;
                    border: 1px solid transparent;
                    font-weight: 700;
                }
                .limit-value-pill.daily {
                    color: #86efac;
                    background: rgba(34, 197, 94, 0.16);
                    border-color: rgba(34, 197, 94, 0.35);
                }
                .limit-value-pill.hourly {
                    color: #fcd34d;
                    background: rgba(245, 158, 11, 0.16);
                    border-color: rgba(245, 158, 11, 0.35);
                }
                .limit-value-pill.rank-1,
                .destinations-pill.rank-1 { box-shadow: 0 0 14px rgba(244, 63, 94, 0.45); border-color: #fb7185; }
                .limit-value-pill.rank-2,
                .destinations-pill.rank-2 { box-shadow: 0 0 13px rgba(249, 115, 22, 0.42); border-color: #fb923c; }
                .limit-value-pill.rank-3,
                .destinations-pill.rank-3 { box-shadow: 0 0 12px rgba(250, 204, 21, 0.4); border-color: #facc15; }
                .limit-value-pill.rank-4,
                .destinations-pill.rank-4 { box-shadow: 0 0 11px rgba(132, 204, 22, 0.38); border-color: #a3e635; }
                .limit-value-pill.rank-5,
                .destinations-pill.rank-5 { box-shadow: 0 0 10px rgba(16, 185, 129, 0.35); border-color: #34d399; }
                .limit-value-pill.rank-6,
                .destinations-pill.rank-6 { box-shadow: 0 0 9px rgba(20, 184, 166, 0.33); border-color: #2dd4bf; }
                .limit-value-pill.rank-7,
                .destinations-pill.rank-7 { box-shadow: 0 0 8px rgba(6, 182, 212, 0.3); border-color: #22d3ee; }
                .limit-value-pill.rank-8,
                .destinations-pill.rank-8 { box-shadow: 0 0 7px rgba(14, 165, 233, 0.27); border-color: #38bdf8; }
                .limit-value-pill.rank-9,
                .destinations-pill.rank-9 { box-shadow: 0 0 6px rgba(59, 130, 246, 0.24); border-color: #60a5fa; }
                .limit-value-pill.rank-10,
                .destinations-pill.rank-10 { box-shadow: 0 0 5px rgba(99, 102, 241, 0.22); border-color: #818cf8; }
                .popup-limit-pill {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 6px;
                    border: 1px solid transparent;
                }
                .popup-limit-pill.daily {
                    color: #86efac;
                    background: rgba(34, 197, 94, 0.12);
                    border-color: rgba(34, 197, 94, 0.3);
                }
                .popup-limit-pill.hourly {
                    color: #fcd34d;
                    background: rgba(245, 158, 11, 0.12);
                    border-color: rgba(245, 158, 11, 0.3);
                }
                .popup-limit-pill.top {
                    box-shadow: 0 0 10px rgba(34, 211, 238, 0.28);
                }
                .destination-name {
                    color: #cbd5e1;
                }
                .destination-name-top {
                    color: #22d3ee;
                    font-weight: 700;
                }
                .destinations-pill {
                    display: inline-block;
                    min-width: 34px;
                    text-align: center;
                    padding: 2px 8px;
                    border-radius: 999px;
                    font-family: 'Space Mono', monospace;
                    font-size: 11px;
                    font-weight: 700;
                    border: 1px solid transparent;
                }
                .destinations-pill.tier-low {
                    color: #cbd5e1;
                    background: rgba(71, 85, 105, 0.2);
                    border-color: rgba(148, 163, 184, 0.25);
                }
                .destinations-pill.tier-mid {
                    color: #2dd4bf;
                    background: rgba(45, 212, 191, 0.16);
                    border-color: rgba(45, 212, 191, 0.36);
                }
                .destinations-pill.tier-high {
                    color: #fbbf24;
                    background: rgba(251, 191, 36, 0.16);
                    border-color: rgba(251, 191, 36, 0.35);
                }
                .destinations-pill.tier-max {
                    color: #fb7185;
                    background: rgba(251, 113, 133, 0.18);
                    border-color: rgba(251, 113, 133, 0.38);
                    box-shadow: 0 0 10px rgba(251, 113, 133, 0.18);
                }
                .severity-pill {
                    display: inline-block;
                    padding: 2px 10px;
                    border-radius: 999px;
                    border: 1px solid transparent;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }
                .severity-pill.critical {
                    color: #fecaca;
                    background: rgba(239, 68, 68, 0.16);
                    border-color: rgba(239, 68, 68, 0.45);
                }
                .severity-pill.warning {
                    color: #fde68a;
                    background: rgba(245, 158, 11, 0.16);
                    border-color: rgba(245, 158, 11, 0.45);
                }
                .severity-pill.info {
                    color: #93c5fd;
                    background: rgba(59, 130, 246, 0.16);
                    border-color: rgba(59, 130, 246, 0.45);
                }
                .reason-cell {
                    display: inline-block;
                    max-width: 320px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #94a3b8;
                    font-size: 11px;
                }
                .compare-window-note {
                    margin-top: 8px;
                    font-size: 11px;
                    color: #64748b;
                    font-family: 'Space Mono', monospace;
                }
                .compare-window-note span {
                    color: #22d3ee;
                }
                .limits-filter-bar {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 10px;
                    flex-wrap: wrap;
                }
                .growth-panels {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 10px;
                    margin-bottom: 10px;
                }
                .destination-summary-panels {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                }
                .growth-panel {
                    background: #0b1220;
                    border: 1px solid #1e293b;
                    border-radius: 8px;
                    padding: 10px;
                }
                .growth-title {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #22d3ee;
                    margin-bottom: 8px;
                    font-family: 'Space Mono', monospace;
                }
                .growth-list {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .growth-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                }
                .growth-name {
                    color: #cbd5e1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .growth-value {
                    color: #34d399;
                    font-family: 'Space Mono', monospace;
                    font-weight: 700;
                }
                .limits-filter-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #94a3b8;
                    font-size: 11px;
                    font-family: 'Space Mono', monospace;
                }
                .limits-filter-item select {
                    background: #0e1420;
                    border: 1px solid #263044;
                    color: #e2e8f0;
                    border-radius: 6px;
                    font-size: 11px;
                    padding: 5px 8px;
                    font-family: 'Space Mono', monospace;
                }
                .access-meta {
                    font-family: 'Space Mono', monospace;
                    font-size: 10px;
                    color: #475569;
                    margin-bottom: 12px;
                }
                .access-meta span {
                    color: #94a3b8;
                }
                .tab-bar {
                    display: flex;
                    align-items: stretch;
                    gap: 0;
                }
                .tab-btn {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    padding: 8px 16px;
                    border-radius: 6px 6px 0 0;
                    border: 1px solid transparent;
                    border-bottom: none;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                    position: relative;
                    bottom: -1px;
                    white-space: nowrap;
                }
                .tab-btn .cnt {
                    font-family: 'Space Mono', monospace;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 1px 7px;
                    border-radius: 20px;
                }
                .tab-btn.new-sids,
                .tab-btn.disappeared {
                    background: #0e1420;
                    color: #94a3b8;
                    border-color: #1c2438;
                }
                .tab-btn.new-sids .cnt,
                .tab-btn.disappeared .cnt {
                    background: #263044;
                    color: #475569;
                }
                .tab-btn.new-sids.active {
                    background: #10b981;
                    color: #000;
                    border-color: #10b981;
                }
                .tab-btn.new-sids.active .cnt {
                    background: rgba(0, 0, 0, 0.2);
                    color: #000;
                }
                .tab-btn.disappeared {
                    margin-left: 4px;
                }
                .tab-btn.disappeared.active {
                    background: #ef4444;
                    color: #000;
                    border-color: #ef4444;
                }
                .tab-btn.disappeared.active .cnt {
                    background: rgba(0, 0, 0, 0.2);
                    color: #000;
                }
                .tab-btn.disappeared:hover {
                    color: #e2e8f0;
                }
                .search-wrap {
                    flex: 1;
                    margin-left: 8px;
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .search-wrap svg {
                    position: absolute;
                    left: 11px;
                    width: 14px;
                    height: 14px;
                    color: #475569;
                }
                .search-input {
                    width: 100%;
                    background: #0e1420;
                    border: 1px solid #1c2438;
                    border-radius: 6px 6px 0 0;
                    border-bottom: none;
                    color: #e2e8f0;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 12px;
                    padding: 8px 12px 8px 32px;
                    outline: none;
                    transition: border-color 0.15s;
                }
                .search-input::placeholder {
                    color: #475569;
                }
                .search-input:focus {
                    border-color: #263044;
                }
                .main {
                    padding: 20px 24px 32px;
                }
                .topbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                }
                .logo {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .logo-mark {
                    width: 30px;
                    height: 30px;
                    background: linear-gradient(135deg, #3b82f6, #06b6d4);
                    border-radius: 7px;
                    display: grid;
                    place-items: center;
                    font-size: 12px;
                    font-weight: 700;
                }
                .logo-text {
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0.04em;
                }
                .logo-sub {
                    font-family: 'Space Mono', monospace;
                    font-size: 9px;
                    color: #475569;
                    margin-top: 2px;
                }
                .kpis {
                    display: flex;
                    gap: 2px;
                }
                .kpi {
                    padding: 6px 16px;
                    background: #0e1420;
                    border: 1px solid #1c2438;
                    text-align: center;
                }
                .kpi:first-child { border-radius: 7px 0 0 7px; }
                .kpi:last-child { border-radius: 0 7px 7px 0; }
                .kpi .v {
                    font-family: 'Space Mono', monospace;
                    font-size: 16px;
                    font-weight: 700;
                    color: #06b6d4;
                }
                .kpi .l {
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: 0.1em;
                    color: #475569;
                    margin-top: 1px;
                }
                .section-label {
                    font-family: 'Space Mono', monospace;
                    font-size: 9px;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    color: #475569;
                    margin-bottom: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .section-label::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: #1c2438;
                }
                .grid {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 10px;
                }
                .card {
                    background: #0e1420;
                    border: 1px solid #1c2438;
                    border-radius: 9px;
                    overflow: hidden;
                    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
                    position: relative;
                    animation: rise 0.35s ease both;
                }
                .card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: linear-gradient(90deg, #3b82f6, #06b6d4);
                    opacity: 0;
                    transition: opacity 0.2s;
                }
                .card:hover {
                    border-color: #263044;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
                }
                .card:hover::before { opacity: 1; }
                .card-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 9px 12px 8px;
                    border-bottom: 1px solid #1c2438;
                }
                .supplier {
                    font-weight: 600;
                    font-size: 13px;
                    color: rgb(245, 158, 11);
                }
                .badges {
                    display: flex;
                    gap: 5px;
                    align-items: center;
                }
                .badge {
                    font-family: 'Space Mono', monospace;
                    font-size: 9px;
                    font-weight: 700;
                    padding: 2px 7px;
                    border-radius: 4px;
                    border: 1px solid;
                }
                .badge.rows {
                    color: #475569;
                    border-color: #263044;
                    background: transparent;
                }
                .badge.dest-single {
                    color: #10b981;
                    border-color: rgba(16, 185, 129, 0.3);
                    background: rgba(16, 185, 129, 0.08);
                }
                .badge.dest-multi {
                    color: #f59e0b;
                    border-color: rgba(245, 158, 11, 0.3);
                    background: rgba(245, 158, 11, 0.08);
                }
                .sid-copy-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 22px;
                    height: 22px;
                    border-radius: 5px;
                    border: 1px solid #263044;
                    background: transparent;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: border-color 0.15s, color 0.15s, background 0.15s;
                }
                .sid-copy-btn:hover {
                    border-color: #06b6d4;
                    color: #06b6d4;
                    background: rgba(6, 182, 212, 0.08);
                }
                .card-body {
                    padding: 6px 12px 8px;
                }
                .route {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 3px 4px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
                    gap: 8px;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: background 0.12s;
                }
                .route:last-child { border-bottom: none; padding-bottom: 0; }
                .route:first-child { padding-top: 0; }
                .route:hover { background: rgba(59, 130, 246, 0.07); }
                .route:hover .route-country { color: #06b6d4; }
                .route-left {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    min-width: 0;
                }
                .route-info {
                    min-width: 0;
                    display: flex;
                    align-items: baseline;
                    gap: 4px;
                }
                .route-country {
                    font-size: 11px;
                    font-weight: 500;
                    color: #e2e8f0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    transition: color 0.12s;
                }
                .route-sep {
                    font-size: 9px;
                    color: #263044;
                    flex-shrink: 0;
                }
                .route-carrier {
                    font-size: 10px;
                    color: #475569;
                    font-family: 'Space Mono', monospace;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex-shrink: 1;
                }
                .route-right {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    flex-shrink: 0;
                }
                .count-pill {
                    font-family: 'Space Mono', monospace;
                    font-size: 10px;
                    font-weight: 700;
                    padding: 1px 7px;
                    border-radius: 20px;
                    background: rgba(59, 130, 246, 0.1);
                    border: 1px solid rgba(59, 130, 246, 0.2);
                    color: #3b82f6;
                }
                .count-pill.high {
                    background: rgba(6, 182, 212, 0.1);
                    border-color: rgba(6, 182, 212, 0.25);
                    color: #06b6d4;
                }
                .expand-icon {
                    font-size: 10px;
                    color: #475569;
                    opacity: 0;
                    transition: opacity 0.12s;
                    flex-shrink: 0;
                }
                .route:hover .expand-icon { opacity: 1; }
                .overlay {
                    display: none;
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.65);
                    backdrop-filter: blur(4px);
                    z-index: 100;
                    align-items: center;
                    justify-content: center;
                }
                .overlay.open { display: flex; }
                .popup {
                    background: #111827;
                    border: 1px solid #263044;
                    border-radius: 12px;
                    width: 480px;
                    max-width: 92vw;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.7);
                    animation: popIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) both;
                }
                .popup-head {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    padding: 16px 18px 12px;
                    border-bottom: 1px solid #1c2438;
                    flex-shrink: 0;
                }
                .popup-title {
                    font-size: 15px;
                    font-weight: 700;
                }
                .popup-sub {
                    font-family: 'Space Mono', monospace;
                    font-size: 10px;
                    color: #475569;
                    margin-top: 3px;
                }
                .popup-close {
                    background: #1c2438;
                    border: none;
                    color: #94a3b8;
                    width: 26px;
                    height: 26px;
                    border-radius: 6px;
                    font-size: 14px;
                    cursor: pointer;
                    display: grid;
                    place-items: center;
                    transition: background 0.15s;
                    flex-shrink: 0;
                }
                .popup-close:hover {
                    background: #263044;
                    color: #e2e8f0;
                }
                .popup-body {
                    padding: 14px 18px 18px;
                    overflow-y: auto;
                    flex: 1;
                }
                .popup-body::-webkit-scrollbar { width: 4px; }
                .popup-body::-webkit-scrollbar-thumb {
                    background: #263044;
                    border-radius: 2px;
                }
                .popup-stats {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .pstat {
                    flex: 1 1 140px;
                    background: #0e1420;
                    border: 1px solid #1c2438;
                    border-radius: 7px;
                    padding: 8px 12px;
                    text-align: center;
                }
                .pstat .pv {
                    font-family: 'Space Mono', monospace;
                    font-size: 16px;
                    font-weight: 700;
                    color: #06b6d4;
                }
                .pstat .pl {
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #475569;
                    margin-top: 2px;
                }
                .pstat.pstat-limit {
                    border-color: rgba(250, 204, 21, 0.35);
                    background: rgba(245, 158, 11, 0.1);
                }
                .pstat.pstat-limit .pv {
                    color: #facc15;
                }
                .range-section-label {
                    font-family: 'Space Mono', monospace;
                    font-size: 9px;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    color: #475569;
                    margin: 14px 0 10px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .range-section-label::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: #1c2438;
                }
                .range-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
                    gap: 6px;
                }
                .range-chip {
                    background: #0e1420;
                    border: 1px solid #1c2438;
                    border-radius: 5px;
                    padding: 5px 10px;
                    font-family: 'Space Mono', monospace;
                    font-size: 11px;
                    color: #94a3b8;
                    text-align: center;
                    cursor: pointer;
                    transition: border-color 0.12s, color 0.12s, background 0.12s;
                    user-select: none;
                }
                .range-chip:hover {
                    border-color: #3b82f6;
                    color: #e2e8f0;
                }
                .range-chip:active {
                    background: rgba(59, 130, 246, 0.15);
                }
                .copy-all-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: transparent;
                    border: 1px solid #263044;
                    color: #94a3b8;
                    border-radius: 5px;
                    padding: 3px 10px;
                    font-family: 'Space Mono', monospace;
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 0.06em;
                    cursor: pointer;
                    transition: border-color 0.15s, color 0.15s, background 0.15s;
                    white-space: nowrap;
                }
                .copy-all-btn:hover {
                    border-color: #06b6d4;
                    color: #06b6d4;
                }
                .limits-details-popup {
                    width: 760px;
                }
                .limits-detail-table-wrap {
                    background: #0b1220;
                    border: 1px solid #1e293b;
                    border-radius: 8px;
                    overflow: auto;
                }
                .limits-detail-table {
                    width: 100%;
                    border-collapse: collapse;
                    min-width: 620px;
                }
                .limits-detail-table th,
                .limits-detail-table td {
                    padding: 10px 12px;
                    border-bottom: 1px solid #1e293b;
                    text-align: left;
                    font-size: 12px;
                }
                .limits-detail-table th {
                    font-size: 11px;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    color: #93c5fd;
                    background: #0f172a;
                    position: sticky;
                    top: 0;
                }
                .limits-popup-filter-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 10px;
                    flex-wrap: wrap;
                }
                .limits-popup-search {
                    position: relative;
                    display: flex;
                    align-items: center;
                    min-width: 250px;
                    flex: 1;
                }
                .limits-popup-search svg {
                    position: absolute;
                    left: 10px;
                    color: #475569;
                }
                .limits-popup-search input {
                    width: 100%;
                    background: #0e1420;
                    border: 1px solid #263044;
                    border-radius: 6px;
                    color: #e2e8f0;
                    font-size: 12px;
                    padding: 7px 10px 7px 30px;
                    font-family: 'DM Sans', sans-serif;
                    outline: none;
                }
                .limits-popup-search input:focus {
                    border-color: #3b82f6;
                }
                .limits-filter-item.compact {
                    flex-shrink: 0;
                }
                .limits-detail-table tbody tr:hover {
                    background: rgba(56, 189, 248, 0.05);
                }
                .toast {
                    position: fixed;
                    bottom: 24px;
                    right: 24px;
                    background: #1a2236;
                    border: 1px solid #10b981;
                    color: #10b981;
                    font-family: 'Space Mono', monospace;
                    font-size: 11px;
                    border-radius: 7px;
                    padding: 9px 16px;
                    z-index: 200;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
                    opacity: 0;
                    transform: translateY(8px);
                    transition: opacity 0.2s, transform 0.2s;
                    pointer-events: none;
                }
                .toast.show {
                    opacity: 1;
                    transform: translateY(0);
                }
                @keyframes rise {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes popIn {
                    from { opacity: 0; transform: scale(0.92) translateY(12px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes sidFlash {
                    0%, 100% { text-shadow: 0 0 0 rgba(245, 158, 11, 0); }
                    50% { text-shadow: 0 0 10px rgba(245, 158, 11, 0.55); }
                }
                @media (max-width: 900px) {
                    .access-actions {
                        flex-direction: column;
                        align-items: flex-end;
                        gap: 6px;
                    }
                    .refresh-timer {
                        min-width: 0;
                    }
                    .growth-panels {
                        grid-template-columns: minmax(0, 1fr);
                    }
                }
                @media (max-width: 700px) {
                    .kpis { display: none; }
                }
                @media (max-width: 1200px) {
                    .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                }
                @media (max-width: 900px) {
                    .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                }
                @media (max-width: 600px) {
                    .grid { grid-template-columns: minmax(0, 1fr); }
                }
                `}
            </style>
        </div>
    );
};

export default AccessListChanges;
